import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import API from '@/lib/api'
import { computeIBANFromCzechBankAccount } from '@/lib/computeIBANFromCzechBankAccount'
import { components } from '@invo.space/shared'

import {
  bankAccountFormSchema,
  BankAccountFormValues,
} from './schema'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

interface InvoicingBankAccountFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingAccount: InvoicingBankAccount | null
  onSuccess: () => void
}

const defaultValues: BankAccountFormValues = {
  name: '',
  accountNumber: '',
  bankCode: '',
  iban: '',
  bic: '',
  isDefault: false,
}

export function InvoicingBankAccountForm({
  open,
  onOpenChange,
  editingAccount,
  onSuccess,
}: InvoicingBankAccountFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'settings.bankAccounts',
  })

  const methods = useForm<BankAccountFormValues>({
    resolver: zodResolver(bankAccountFormSchema),
    defaultValues,
  })

  useEffect(() => {
    if (!open) return
    if (editingAccount) {
      methods.reset({
        name: editingAccount.name ?? '',
        accountNumber: editingAccount.accountNumber ?? '',
        bankCode: editingAccount.bankCode ?? '',
        iban: editingAccount.iban ?? '',
        bic: editingAccount.bic ?? '',
        isDefault: editingAccount.isDefault ?? false,
      })
    } else {
      methods.reset(defaultValues)
    }
  }, [open, editingAccount, methods])

  const accountNumber = methods.watch('accountNumber')
  const bankCode = methods.watch('bankCode')

  useEffect(() => {
    if (!accountNumber || !bankCode) return
    const [prefix = '', number = ''] = accountNumber.includes('-')
      ? accountNumber.split('-', 2)
      : ['', accountNumber]
    if (!number) return
    try {
      const iban = computeIBANFromCzechBankAccount(prefix, number, bankCode)
      methods.setValue('iban', iban)
    } catch {
      // invalid input, skip
    }
  }, [accountNumber, bankCode, methods])

  const onSubmit = async (values: BankAccountFormValues) => {
    try {
      if (editingAccount) {
        await API.patch(
          `/api/v2/bank-accounts/${editingAccount.id}`,
          values as components['schemas']['UpdateInvoicingBankAccountRequest'],
        )
        toast.success(t('edit.success'))
      } else {
        await API.post(
          '/api/v2/bank-accounts',
          values as components['schemas']['CreateInvoicingBankAccountRequest'],
        )
        toast.success(t('create.success'))
      }
      onOpenChange(false)
      onSuccess()
    } catch {
      toast.error(t(editingAccount ? 'edit.error' : 'create.error'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingAccount ? t('edit.title') : t('create.title')}
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={methods.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('fields.namePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-2 items-start gap-3">
              <FormField
                control={methods.control}
                name="accountNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.accountNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="bankCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.bankCode')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 items-start gap-3">
              <FormField
                control={methods.control}
                name="iban"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.iban')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} className="tabular" />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Dopočítá se z čísla účtu.
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={methods.control}
                name="bic"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('fields.bic')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={methods.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 rounded-lg bg-muted/70 px-4 py-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-0.5"
                    />
                  </FormControl>
                  <div className="space-y-0.5">
                    <FormLabel className="font-medium">{t('fields.isDefault')}</FormLabel>
                    <FormDescription>
                      Předvyplní se na nových fakturách a výzvách k platbě.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t('actions.cancel')}
              </Button>
              <Button type="submit">{t('actions.save')}</Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
